import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Monitor,
  Camera,
  Mic,
  MicOff,
  CameraOff,
  Users,
  Copy,
  Check,
  Zap,
  LogOut,
  ScreenShare,
  ScreenShareOff,
  BrainCircuit,
  Trash2,
  Loader2,
  MessageSquare,
  XCircle,
  Square,
} from 'lucide-react';

interface Student {
  id: string;
  name: string;
}

interface ActiveQuiz {
  question: string;
  options: string[];
  correctIndex: number;
}

interface QuizResults {
  answered: number;
  totalStudents: number;
  correct: number;
  incorrect: number;
  percentage: number;
}

interface StudentCameraFrame {
  buffer: number[];
  ts: number;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

const WS_URL =
  import.meta.env.VITE_WS_URL ||
  (
    (typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws') +
    '://' +
    (typeof window !== 'undefined' ? window.location.host : 'localhost:3000') +
    '/ws/relay?role=teacher'
  );

export default function TeacherPage() {
  const [roomId, setRoomId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [studentCount, setStudentCount] = useState(0);
  const [screenSharing, setScreenSharing] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [connected, setConnected] = useState(false);

  // Transcription + Quiz state
  const [transcript, setTranscript] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<ActiveQuiz | null>(null);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResults | null>(null);
  const [classEnded, setClassEnded] = useState(false);

  // Student list and camera state
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentCameraFrame, setStudentCameraFrame] = useState<StudentCameraFrame | null>(null);

  // Room name input state
  const [roomName, setRoomName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [roomNameTaken, setRoomNameTaken] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const screenIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cameraIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recognitionRef = useRef<any | null>(null);
  const transcriptTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const studentCameraImgRef = useRef<HTMLImageElement | null>(null);
  const studentCameraFramesRef = useRef(new Map<string, StudentCameraFrame>());

  // Connect WebSocket on mount
  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      const savedRoomId = typeof window !== 'undefined' ? localStorage.getItem('edupulse-teacher-room') : null;
      if (savedRoomId) {
        ws.send(JSON.stringify({ type: 'resume-room', roomId: savedRoomId }));
      } else {
        setShowNameInput(true);
      }
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'room-created') {
          setRoomId(msg.roomId);
          sessionIdRef.current = msg.roomId;
          localStorage.setItem('edupulse-teacher-room', msg.roomId);
        } else if (msg.type === 'room-resumed') {
          setRoomId(msg.roomId);
          sessionIdRef.current = msg.roomId;
          localStorage.setItem('edupulse-teacher-room', msg.roomId);
        } else if (msg.type === 'room-not-found') {
          localStorage.removeItem('edupulse-teacher-room');
          setShowNameInput(true);
        } else if (msg.type === 'room-name-taken') {
          setRoomNameTaken(true);
        } else if (msg.type === 'student-count') {
          setStudentCount(msg.count);
        } else if (msg.type === 'student-list') {
          setStudents(msg.students);
          setStudentCount(msg.count);
          const currentIds = new Set(msg.students.map((s: Student) => s.id));
          for (const [id] of studentCameraFramesRef.current) {
            if (!currentIds.has(id)) {
              studentCameraFramesRef.current.delete(id);
            }
          }
        } else if (msg.type === 'student-camera') {
          studentCameraFramesRef.current.set(msg.studentId, msg.data);
          if (selectedStudent && msg.studentId === selectedStudent.id) {
            setStudentCameraFrame(msg.data);
          }
        } else if (msg.type === 'quiz-results') {
          setQuizResults(msg);
        }
      } catch {}
    };

    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    return () => {
      ws.close();
      stopScreenShare();
      stopCamera();
      stopMic();
      stopTranscription();
      if (transcriptTimerRef.current) clearInterval(transcriptTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (studentCameraFrame?.buffer && studentCameraImgRef.current) {
      const blob = new Blob([new Uint8Array(studentCameraFrame.buffer)], {
        type: 'image/jpeg',
      });
      const prev = studentCameraImgRef.current.src;
      studentCameraImgRef.current.src = URL.createObjectURL(blob);
      if (prev) URL.revokeObjectURL(prev);
    }
  }, [studentCameraFrame]);

  const sendWs = useCallback((msg: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const createRoomWithName = () => {
    const name = roomName.trim();
    if (!name) return;
    setRoomNameTaken(false);
    sendWs({ type: 'create-room', name });
  };

  const sendFrame = useCallback(
    (type: string, data: Record<string, unknown>) => {
      sendWs({ type, data });
    },
    [sendWs]
  );

  // --- Screen Share ---
  const startScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      screenStreamRef.current = stream;

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      stream.getVideoTracks()[0].onended = () => stopScreenShare();

      screenIntervalRef.current = setInterval(() => {
        if (video.readyState < 2) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx!.drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            const reader = new FileReader();
            reader.onload = () => {
              sendFrame('screen-frame', {
                buffer: Array.from(new Uint8Array(reader.result as ArrayBuffer)),
                ts: Date.now(),
              });
            };
            reader.readAsArrayBuffer(blob);
          },
          'image/jpeg',
          0.5
        );
      }, 200);

      setScreenSharing(true);
    } catch (err) {
      console.error('Screen share failed:', err);
    }
  };

  const stopScreenShare = () => {
    if (screenIntervalRef.current) {
      clearInterval(screenIntervalRef.current);
      screenIntervalRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    setScreenSharing(false);
  };

  // --- Camera ---
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      cameraStreamRef.current = stream;

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      cameraIntervalRef.current = setInterval(() => {
        if (video.readyState < 2) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx!.drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            const reader = new FileReader();
            reader.onload = () => {
              sendFrame('camera-frame', {
                buffer: Array.from(new Uint8Array(reader.result as ArrayBuffer)),
                ts: Date.now(),
              });
            };
            reader.readAsArrayBuffer(blob);
          },
          'image/jpeg',
          0.5
        );
      }, 200);

      setCameraOn(true);
    } catch (err) {
      console.error('Camera failed:', err);
    }
  };

  const stopCamera = () => {
    if (cameraIntervalRef.current) {
      clearInterval(cameraIntervalRef.current);
      cameraIntervalRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
    setCameraOn(false);
  };

  // --- Mic + Audio Streaming ---
  const startMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const audioCtx = new AudioContext({ sampleRate: 16000 });
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        const inputData = e.inputBuffer.getChannelData(0);
        const downsampled = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          downsampled[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        sendFrame('audio-chunk', {
          buffer: Array.from(downsampled),
          ts: Date.now(),
        });
      };

      source.connect(processor);
      processor.connect(audioCtx.destination);

      stream.getAudioTracks()[0].onended = () => stopMic();
      setMicOn(true);

      // Also start speech recognition for transcription
      startTranscription(stream);
    } catch (err) {
      console.error('Mic failed:', err);
    }
  };

  const stopMic = () => {
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    stopTranscription();
    setMicOn(false);
  };

  // --- Speech Recognition (Transcription) ---
  const startTranscription = (_audioStream: MediaStream) => {
    const SpeechRecognitionClass =
      typeof window !== 'undefined' &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);

    if (!SpeechRecognitionClass) {
      console.warn('Speech Recognition not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognitionClass();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = transcript;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += ' ' + text;
        } else {
          interim += text;
        }
      }
      setTranscript(finalTranscript + (interim ? ' ' + interim : ''));
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setTranscribing(false);
      }
    };

    recognition.onend = () => {
      // Auto-restart if mic is still on
      if (micStreamRef.current && recognitionRef.current) {
        try {
          recognition.start();
        } catch {}
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setTranscribing(true);
    } catch (err) {
      console.error('Failed to start recognition:', err);
    }
  };

  const stopTranscription = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
      recognitionRef.current = null;
    }
    setTranscribing(false);
  };

  // Send transcript to server periodically
  useEffect(() => {
    transcriptTimerRef.current = setInterval(() => {
      if (transcript.trim() && sessionIdRef.current) {
        fetch(API_BASE + '/api/transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sessionIdRef.current,
            transcript: transcript.trim(),
          }),
        }).catch(() => {});
      }
    }, 5000);

    return () => {
      if (transcriptTimerRef.current) clearInterval(transcriptTimerRef.current);
    };
  }, [transcript]);

  // --- Quiz ---
  const generateQuiz = async () => {
    if (!transcript.trim() || !sessionIdRef.current) return;

    setGeneratingQuiz(true);
    setQuizError(null);

    try {
      const res = await fetch(API_BASE + '/api/trigger-quiz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          transcriptSlice: transcript.slice(-3000),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setQuizError(data.error || 'Failed to generate quiz');
        return;
      }

      setActiveQuiz(data.quiz);
      setQuizResults(null);
      sendWs({ type: 'quiz-active', quiz: data.quiz });
    } catch (err) {
      setQuizError('Network error. Please try again.');
    } finally {
      setGeneratingQuiz(false);
    }
  };

  const clearQuiz = async () => {
    if (!sessionIdRef.current) return;

    try {
      await fetch(API_BASE + '/api/clear-quiz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionIdRef.current }),
      });
    } catch {}

    setActiveQuiz(null);
    setQuizError(null);
    setQuizResults(null);
    sendWs({ type: 'clear-quiz' });
  };

  const copyRoomId = () => {
    if (roomId) {
      navigator.clipboard.writeText(roomId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const stopClass = () => {
    if (!window.confirm('Are you sure you want to end this class? All students will be disconnected.')) {
      return;
    }
    sendWs({ type: 'stop-class' });
    stopScreenShare();
    stopCamera();
    stopMic();
    stopTranscription();
    if (transcriptTimerRef.current) clearInterval(transcriptTimerRef.current);
    localStorage.removeItem('edupulse-teacher-room');
    setClassEnded(true);
    setRoomId(null);
    setStudentCount(0);
    setRoomName('');
    setShowNameInput(false);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {classEnded ? (
        <div className="flex items-center justify-center h-screen">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-emerald-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Square size={32} className="text-emerald-400" fill="currentColor" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Class Ended</h2>
            <p className="text-slate-400 mb-6">
              The session has been stopped. All students have been disconnected.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition"
            >
              <LogOut size={16} /> Back to Home
            </a>
          </div>
        </div>
      ) : (
      <>
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="text-white" size={18} fill="currentColor" />
            </div>
            <span className="text-lg font-bold">EduPulse AI</span>
            <span className="text-xs bg-indigo-600/20 text-indigo-400 px-2 py-0.5 rounded-full font-medium">
              Teacher
            </span>
          </div>

          {roomId && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-4 py-2">
                <span className="text-xs text-slate-400">Room</span>
                <span className="font-mono font-bold text-lg tracking-wider text-indigo-400">
                  {roomId}
                </span>
                <button
                  onClick={copyRoomId}
                  className="text-slate-400 hover:text-white transition ml-1"
                  title="Copy room code"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-700/50 rounded-lg px-3 py-2">
                <Users size={16} className="text-emerald-400" />
                <span className="text-sm font-medium">{studentCount}</span>
                <span className="text-xs text-slate-400">
                  {studentCount === 1 ? 'student' : 'students'}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  connected ? 'bg-emerald-400' : 'bg-red-400'
                }`}
              />
              <span className="text-xs text-slate-400">
                {connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            {roomId && (
              <button
                onClick={stopClass}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30 transition"
              >
                <Square size={14} fill="currentColor" /> End Class
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {!roomId ? (
          <div className="flex items-center justify-center h-[60vh]">
            {showNameInput ? (
              <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
                <div className="w-12 h-12 bg-indigo-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap size={24} className="text-indigo-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Create a Room</h2>
                <p className="text-slate-400 mb-4">
                  Enter a name for your classroom
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    createRoomWithName();
                  }}
                  className="space-y-4"
                >
                  <input
                    type="text"
                    value={roomName}
                    onChange={(e) => {
                      setRoomName(e.target.value);
                      setRoomNameTaken(false);
                    }}
                    placeholder="e.g. CS101 Lecture, Math Class"
                    maxLength={30}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    autoFocus
                  />
                  {roomNameTaken && (
                    <p className="text-xs text-red-400">
                      This room name is already taken. Please choose another.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={!roomName.trim()}
                    className="w-full px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Create Room
                  </button>
                </form>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Connecting...</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stream Previews */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Screen Share Preview */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                  <div className="flex items-center gap-2">
                    <Monitor size={16} className="text-indigo-400" />
                    <span className="text-sm font-medium">Screen Share</span>
                  </div>
                  <button
                    onClick={screenSharing ? stopScreenShare : startScreenShare}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      screenSharing
                        ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                        : 'bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30'
                    }`}
                  >
                    {screenSharing ? (
                      <span className="flex items-center gap-1">
                        <ScreenShareOff size={14} /> Stop
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <ScreenShare size={14} /> Share Screen
                      </span>
                    )}
                  </button>
                </div>
                <div className="aspect-video bg-slate-900 flex items-center justify-center">
                  {screenSharing ? (
                    <video
                      ref={(el) => {
                        if (el && screenStreamRef.current) {
                          el.srcObject = screenStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      className="w-full h-full object-contain"
                      muted
                      autoPlay
                    />
                  ) : (
                    <div className="text-center text-slate-500">
                      <Monitor size={48} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Click "Share Screen" to begin</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Camera Preview */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                  <div className="flex items-center gap-2">
                    <Camera size={16} className="text-violet-400" />
                    <span className="text-sm font-medium">Camera</span>
                  </div>
                  <button
                    onClick={cameraOn ? stopCamera : startCamera}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      cameraOn
                        ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                        : 'bg-violet-600/20 text-violet-400 hover:bg-violet-600/30'
                    }`}
                  >
                    {cameraOn ? (
                      <span className="flex items-center gap-1">
                        <CameraOff size={14} /> Stop
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Camera size={14} /> Start Camera
                      </span>
                    )}
                  </button>
                </div>
                <div className="aspect-video bg-slate-900 flex items-center justify-center">
                  {cameraOn ? (
                    <video
                      ref={(el) => {
                        if (el && cameraStreamRef.current) {
                          el.srcObject = cameraStreamRef.current;
                          el.play().catch(() => {});
                        }
                      }}
                      className="w-full h-full object-contain"
                      muted
                      autoPlay
                    />
                  ) : (
                    <div className="text-center text-slate-500">
                      <Camera size={48} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Click "Start Camera" to begin</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Students + Camera Viewer */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Student List */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
                  <Users size={16} className="text-emerald-400" />
                  <span className="text-sm font-medium">Students</span>
                  <span className="text-xs bg-emerald-400/10 text-emerald-400 px-2 py-0.5 rounded-full ml-auto">
                    {students.length}
                  </span>
                </div>
                <div className="p-2 max-h-64 overflow-y-auto">
                  {students.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-4">
                      No students yet
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {students.map((student) => (
                        <button
                          key={student.id}
                          onClick={() => {
                            if (selectedStudent?.id === student.id) {
                              setSelectedStudent(null);
                              setStudentCameraFrame(null);
                            } else {
                              setSelectedStudent(student);
                              const storedFrame = studentCameraFramesRef.current.get(student.id);
                              setStudentCameraFrame(storedFrame || null);
                            }
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition ${
                            selectedStudent?.id === student.id
                              ? 'bg-indigo-600/20 border border-indigo-500/30'
                              : 'hover:bg-slate-700/50'
                          }`}
                        >
                          <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {student.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm text-white truncate">
                            {student.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Student Camera Viewer */}
              <div className="lg:col-span-2 bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
                  <Camera size={16} className="text-violet-400" />
                  <span className="text-sm font-medium">
                    {selectedStudent ? `${selectedStudent.name}'s Camera` : 'Student Camera'}
                  </span>
                  {selectedStudent && (
                    <button
                      onClick={() => {
                        setSelectedStudent(null);
                        setStudentCameraFrame(null);
                      }}
                      className="ml-auto text-xs text-slate-400 hover:text-white transition"
                    >
                      Close
                    </button>
                  )}
                </div>
                <div className="aspect-video bg-slate-900 flex items-center justify-center relative">
                  <img
                    ref={studentCameraImgRef}
                    className={`w-full h-full object-contain ${
                      selectedStudent && studentCameraFrame ? '' : 'hidden'
                    }`}
                    alt="Student camera"
                  />
                  {(!selectedStudent || !studentCameraFrame) && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <Camera size={48} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                          {selectedStudent
                            ? 'Waiting for camera feed...'
                            : 'Select a student to view their camera'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mic + Transcription */}
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {micOn ? (
                    <Mic size={20} className="text-emerald-400" />
                  ) : (
                    <MicOff size={20} className="text-slate-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium">
                      {micOn ? 'Microphone Active' : 'Microphone Off'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {micOn
                        ? transcribing
                          ? 'Transcribing speech to text...'
                          : 'Audio streaming to students'
                        : 'Students cannot hear you'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {transcribing && (
                    <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                      Transcribing
                    </span>
                  )}
                  <button
                    onClick={micOn ? stopMic : startMic}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      micOn
                        ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                        : 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'
                    }`}
                  >
                    {micOn ? 'Mute' : 'Unmute'}
                  </button>
                </div>
              </div>

              {/* Live Transcript */}
              {micOn && (
                <div className="bg-slate-900/50 rounded-xl p-3 max-h-32 overflow-y-auto">
                  <div className="flex items-start gap-2">
                    <MessageSquare
                      size={14}
                      className="text-slate-500 mt-0.5 shrink-0"
                    />
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {transcript || (
                        <span className="italic">
                          Start speaking to see transcript...
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Quiz Section */}
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                <div className="flex items-center gap-2">
                  <BrainCircuit size={16} className="text-amber-400" />
                  <span className="text-sm font-medium">AI Quiz</span>
                  {activeQuiz && (
                    <span className="text-xs bg-amber-400/10 text-amber-400 px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {activeQuiz && (
                    <button
                      onClick={clearQuiz}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30 transition"
                    >
                      <Trash2 size={14} /> Clear
                    </button>
                  )}
                  <button
                    onClick={generateQuiz}
                    disabled={generatingQuiz || !transcript.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {generatingQuiz ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />{' '}
                        Generating...
                      </>
                    ) : (
                      <>
                        <BrainCircuit size={14} /> Generate Quiz
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="p-4">
                {quizError && (
                  <div className="bg-red-600/10 border border-red-600/30 rounded-xl p-3 mb-3">
                    <p className="text-xs text-red-400">{quizError}</p>
                  </div>
                )}

                {activeQuiz ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-white">
                      {activeQuiz.question}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {activeQuiz.options.map((opt, i) => (
                        <div
                          key={i}
                          className={`px-3 py-2 rounded-xl text-xs font-medium border ${
                            i === activeQuiz.correctIndex
                              ? 'bg-emerald-600/20 border-emerald-600/40 text-emerald-400'
                              : 'bg-slate-700/50 border-slate-600/50 text-slate-300'
                          }`}
                        >
                          <span className="text-slate-500 mr-2">
                            {String.fromCharCode(65 + i)}.
                          </span>
                          {opt}
                          {i === activeQuiz.correctIndex && (
                            <span className="ml-2 text-emerald-400">
                              (Correct)
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Quiz Results */}
                    {quizResults && quizResults.answered > 0 && (
                      <div className="mt-4 space-y-3">
                        <div className="border-t border-slate-700 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-slate-400 font-medium">
                              Student Responses
                            </span>
                            <span className="text-xs text-slate-500">
                              {quizResults.answered} / {quizResults.totalStudents} answered
                            </span>
                          </div>

                          {/* Progress bar */}
                          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden mb-3">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                quizResults.percentage >= 50
                                  ? 'bg-emerald-500'
                                  : 'bg-red-500'
                              }`}
                              style={{
                                width: `${
                                  quizResults.totalStudents > 0
                                    ? (quizResults.correct / quizResults.totalStudents) * 100
                                    : 0
                                }%`,
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-xl p-3 text-center">
                              <p className="text-2xl font-bold text-emerald-400">
                                {quizResults.correct}
                              </p>
                              <p className="text-xs text-emerald-400/70 mt-0.5">
                                Correct
                              </p>
                            </div>
                            <div className="bg-red-600/10 border border-red-600/30 rounded-xl p-3 text-center">
                              <p className="text-2xl font-bold text-red-400">
                                {quizResults.incorrect}
                              </p>
                              <p className="text-xs text-red-400/70 mt-0.5">
                                Incorrect
                              </p>
                            </div>
                            <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-3 text-center">
                              <p className="text-2xl font-bold text-slate-300">
                                {quizResults.percentage}%
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">
                                Score
                              </p>
                            </div>
                          </div>

                          {quizResults.totalStudents > 0 &&
                            quizResults.percentage < 50 && (
                              <div className="mt-3 bg-red-600/10 border border-red-600/30 rounded-xl p-3 flex items-start gap-2">
                                <XCircle
                                  size={16}
                                  className="text-red-400 mt-0.5 shrink-0"
                                />
                                <div>
                                  <p className="text-xs font-medium text-red-400">
                                    Low Comprehension Alert
                                  </p>
                                  <p className="text-xs text-red-400/70 mt-0.5">
                                    Less than 50% of students answered correctly.
                                    Consider reviewing this topic.
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <BrainCircuit
                      size={32}
                      className="mx-auto mb-2 text-slate-600"
                    />
                    <p className="text-sm text-slate-500">
                      {transcript.trim()
                        ? 'Click "Generate Quiz" to create a comprehension question from your lecture.'
                        : 'Start speaking to enable quiz generation.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Share Room Code */}
            <div className="bg-gradient-to-r from-indigo-600/10 to-violet-600/10 border border-indigo-500/30 rounded-2xl p-6 text-center">
              <p className="text-sm text-slate-400 mb-2">
                Share this room code with your students
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-mono font-bold tracking-[0.3em] text-indigo-400">
                  {roomId}
                </span>
                <button
                  onClick={copyRoomId}
                  className="p-2 bg-indigo-600/20 rounded-lg text-indigo-400 hover:bg-indigo-600/30 transition"
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Students can join from the landing page using this code
              </p>
            </div>
          </div>
        )}
      </main>
      </>
      )}
    </div>
  );
}
